import { useCallback, useEffect, useState } from 'react';
import {
  fetchAdminFestivalClosure,
  fetchAdminRankingOverview,
  fetchAdminScoringConfig,
  resetAdminRanking,
  resetAdminScoringConfig,
  saveAdminFestivalClosure,
  saveAdminScoringConfig,
} from '../infrastructure/adminApi';
import { ACHIEVEMENTS } from '../../game/data/achievements';

const formatDate = (value) => {
  if (!value) return '—';
  try {
    return new Date(value).toLocaleString('es-MX', { dateStyle: 'medium', timeStyle: 'short' });
  } catch {
    return '—';
  }
};

export default function RankingAdminPanel({ authorization, onSessionExpired }) {
  const [overview, setOverview] = useState(null);
  const [note, setNote] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const [isResetting, setIsResetting] = useState(false);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const [scoring, setScoring] = useState(null);
  const [closure, setClosure] = useState(null);

  const load = useCallback(async () => {
    setIsLoading(true);
    setError('');
    try {
      const [nextOverview, scoringResource, closureResource] = await Promise.all([
        fetchAdminRankingOverview(authorization), fetchAdminScoringConfig(authorization),
        fetchAdminFestivalClosure(authorization),
      ]);
      setOverview(nextOverview);
      setClosure(closureResource.config);
      setScoring(scoringResource.config);
    } catch (requestError) {
      if (requestError.status === 401) onSessionExpired?.();
      else setError(requestError.message);
    } finally {
      setIsLoading(false);
    }
  }, [authorization, onSessionExpired]);

  useEffect(() => {
    load();
  }, [load]);

  const handleReset = async () => {
    setError('');
    setNotice('');
    const archived = overview?.playsTotal ?? 0;
    const seasonStart = overview?.season?.resetAt
      ? formatDate(overview.season.resetAt)
      : 'el inicio';
    const ok = window.confirm(
      `¿Resetear el puntaje?\n\nNo se borra nada: se archivan ${archived} jugadas y el ranking vuelve a calcularse desde hoy (${formatDate(Date.now())}).\nTemporada actual desde ${seasonStart}.\n\nEsta acción abre una temporada nueva.`,
    );
    if (!ok) return;
    setIsResetting(true);
    try {
      const result = await resetAdminRanking(authorization, note.trim());
      setNote('');
      setNotice(
        `Temporada nueva desde ${formatDate(result.season?.resetAt)}. ${result.archivedPlays ?? archived} jugadas archivadas como respaldo; el ranking ya calcula desde esa fecha.`,
      );
      await load();
    } catch (requestError) {
      if (requestError.status === 401) onSessionExpired?.();
      else setError(requestError.message);
    } finally {
      setIsResetting(false);
    }
  };

  const updateClosure = async (changes) => {
    const next = { ...closure, ...changes };
    if ('frozen' in changes && !window.confirm(next.frozen
      ? '¿Congelar los puntos? Nadie podrá sumar puntos; se podrá seguir jugando.'
      : '¿Reabrir los puntos? Las partidas, retos, medallas y fantasmas volverán a sumar.')) return;
    setError(''); setNotice('');
    try {
      const resource = await saveAdminFestivalClosure(authorization, next);
      setClosure(resource.config);
      setNotice(resource.config.frozen
        ? 'Puntos congelados: el ranking ya no cambia.'
        : 'Puntos reabiertos: las próximas acciones vuelven a sumar.');
    } catch (requestError) {
      if (requestError.status === 401) onSessionExpired?.();
      else setError(requestError.message);
    }
  };

  const season = overview?.season;

  const setPoints = (key, value) => setScoring((current) => ({
    ...current,
    ...(key.startsWith('achievement:')
      ? { achievements: { ...current.achievements, [key.slice(12)]: value } }
      : { [key]: value }),
  }));

  const saveScoring = async () => {
    setIsResetting(true); setError(''); setNotice('');
    try {
      const resource = await saveAdminScoringConfig(authorization, scoring);
      setScoring(resource.config);
      setNotice('Puntajes guardados. Solo se aplicarán a premios futuros.');
    } catch (requestError) { setError(requestError.message); }
    finally { setIsResetting(false); }
  };

  const resetScoring = async () => {
    if (!window.confirm('¿Restaurar 100 puntos por medalla, 100 al ganador y 50 al perdedor?')) return;
    setIsResetting(true);
    try { const resource = await resetAdminScoringConfig(authorization); setScoring(resource.config); setNotice('Valores predeterminados restaurados.'); }
    catch (requestError) { setError(requestError.message); }
    finally { setIsResetting(false); }
  };

  return (
    <section className="mx-auto max-w-[1480px] px-5 py-8 lg:px-8">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <p className="font-mono text-[10px] uppercase tracking-[.2em] text-[#9d8cab]">Temporadas y respaldo</p>
          <h2 className="font-display text-3xl uppercase text-white">Puntajes</h2>
          <p className="mt-2 max-w-xl text-sm leading-6 text-[#b7a9c4]">
            El ranking suma premios oficiales de primera visita, medallas, retos y puntos ocultos.
            Resetear <strong className="text-white">no borra</strong> el historial: abre una temporada nueva.
          </p>
        </div>
        <button
          type="button"
          onClick={() => load()}
          disabled={isLoading}
          className="min-h-[44px] border border-[#594a65] px-4 py-2 text-[10px] font-black uppercase tracking-wider text-[#d6c9df] disabled:opacity-60"
        >
          {isLoading ? 'Cargando…' : 'Actualizar'}
        </button>
      </div>

      {error && <p className="mt-4 border border-[#F7E04B]/40 bg-[#2b1219] p-3 text-sm text-[#ffd8de]" role="alert">{error}</p>}
      {notice && <p className="mt-4 border border-[#7EC54A]/50 bg-[#0F1F12] p-3 text-sm text-[#C5E6A0]" role="status">{notice}</p>}

      {closure && (
        <div className="mt-6 border border-[#F7E04B]/50 bg-[#141018] p-4">
          <p className="text-[10px] font-black uppercase tracking-[.16em] text-[#F7E04B]">Cierre del festival</p>
          <p className="mt-2 text-sm leading-6 text-[#b7a9c4]">
            Puntos: <strong className="text-white">{closure.frozen ? 'congelados' : 'abiertos'}</strong>
            {' · '}Review de jugadores: <strong className="text-white">{closure.reviewEnabled ? 'visible' : 'oculto'}</strong>.
            Congelado, nadie suma puntos (partidas, retos, medallas ni fantasmas), pero se puede seguir jugando.
          </p>
          <div className="mt-3 flex flex-wrap gap-2">
            <button
              type="button"
              onClick={() => updateClosure({ frozen: !closure.frozen })}
              className="min-h-[44px] border border-[#F7E04B] px-4 py-2 text-[10px] font-black uppercase tracking-wider text-[#F7E04B]"
            >
              {closure.frozen ? 'Reabrir puntos' : 'Congelar puntos'}
            </button>
            <button
              type="button"
              onClick={() => updateClosure({ reviewEnabled: !closure.reviewEnabled })}
              className="min-h-[44px] border border-[#594a65] px-4 py-2 text-[10px] font-black uppercase tracking-wider text-[#d6c9df]"
            >
              {closure.reviewEnabled ? 'Ocultar review' : 'Mostrar review'}
            </button>
          </div>
        </div>
      )}

      <div className="mt-6 grid gap-3 sm:grid-cols-4">
        {[
          { label: 'Temporada desde', value: season?.resetAt ? formatDate(season.resetAt) : 'El inicio' },
          { label: 'Jugadas en temporada', value: String(overview?.playsInSeason ?? '—') },
          { label: 'Jugadas archivadas', value: String(overview?.playsArchived ?? '—') },
          { label: 'Jugadas totales', value: String(overview?.playsTotal ?? '—') },
        ].map((stat) => (
          <div key={stat.label} className="border border-[#312a3a] bg-[#0d0b11] p-4">
            <p className="text-[10px] font-black uppercase tracking-[.16em] text-[#9d8cab]">{stat.label}</p>
            <p className="mt-2 text-lg font-black text-white">{isLoading ? '…' : stat.value}</p>
          </div>
        ))}
      </div>

      {scoring && <div className="mt-6 border border-[#312a3a] bg-[#0d0b11] p-5">
        <h3 className="font-display text-2xl uppercase text-white">Premios futuros</h3>
        <p className="mt-2 text-sm text-[#b7a9c4]">Cambiar estos valores no modifica premios ya acreditados.</p>
        <div className="mt-5 grid gap-4 sm:grid-cols-2 lg:grid-cols-5">
          {[
            { key: 'challengeWinner', label: 'Ganador de reto', value: scoring.challengeWinner },
            { key: 'challengeLoser', label: 'Perdedor / empate', value: scoring.challengeLoser },
            ...ACHIEVEMENTS.map((achievement) => ({ key: `achievement:${achievement.id}`, label: achievement.title, value: scoring.achievements[achievement.id] })),
          ].map((field) => <label key={field.key} className="text-[10px] font-black uppercase tracking-[.12em] text-[#9d8cab]">{field.label}<input type="number" min="0" max="10000" step="1" value={field.value} onChange={(event) => setPoints(field.key, event.target.value)} className="mt-2 min-h-[44px] w-full border border-[#594a65] bg-black px-3 text-base text-white" /></label>)}
        </div>
        <div className="mt-5 flex flex-wrap gap-3"><button type="button" onClick={saveScoring} disabled={isResetting} className="min-h-[44px] border border-[#7EC54A] bg-[#1A4D2E] px-5 text-xs font-black uppercase text-white disabled:opacity-60">Guardar premios</button><button type="button" onClick={resetScoring} disabled={isResetting} className="min-h-[44px] border border-[#594a65] px-5 text-xs font-black uppercase text-[#d6c9df] disabled:opacity-60">Restaurar defaults</button></div>
      </div>}

      <div className="mt-6 border border-[#5c1a1a] bg-[#160b0d] p-5">
        <h3 className="text-[11px] font-black uppercase tracking-[.18em] text-[#ff9d9d]">Zona de peligro</h3>
        <p className="mt-2 max-w-2xl text-sm leading-6 text-[#d8b6b6]">
          <strong className="text-white">Resetear puntaje</strong> cierra la temporada actual y el
          ranking público vuelve a cero desde este momento. También abandona partidas activas,
          cancela retos incompletos y permite volver a obtener visitas y medallas en la temporada nueva.
        </p>
        <label className="mt-4 block text-[10px] font-black uppercase tracking-[.18em] text-[#9d8cab]" htmlFor="ranking-reset-note">
          Nota de la temporada (opcional)
        </label>
        <input
          id="ranking-reset-note"
          value={note}
          onChange={(event) => setNote(event.target.value.slice(0, 280))}
          maxLength={280}
          placeholder="Ej. Temporada Festival de Terror 2026"
          className="mt-2 w-full max-w-xl border border-[#594a65] bg-[#0d0b11] px-3 py-3 text-sm text-white outline-none placeholder:text-[#6f6280] focus:border-[#ff6b6b]"
        />
        <div className="mt-4">
          <button
            type="button"
            onClick={handleReset}
            disabled={isLoading || isResetting}
            className="min-h-[48px] border border-[#ff2d2d] bg-[#c11212] px-6 py-3 text-[11px] font-black uppercase tracking-[.16em] text-white shadow-[4px_4px_0_rgba(0,0,0,.55)] transition-colors hover:bg-[#a50e0e] disabled:opacity-60"
          >
            {isResetting ? 'Reseteando…' : 'Resetear puntaje'}
          </button>
        </div>
      </div>

      <div className="mt-6 overflow-x-auto border border-[#312a3a] bg-[#0d0b11]">
        <table className="w-full min-w-[560px] text-left text-sm">
          <thead>
            <tr className="border-b border-[#312a3a] text-[10px] uppercase tracking-[.16em] text-[#9d8cab]">
              <th className="px-4 py-3">Temporada desde</th>
              <th className="px-4 py-3">Operador</th>
              <th className="px-4 py-3">Nota</th>
            </tr>
          </thead>
          <tbody>
            {(overview?.resets || []).map((item) => (
              <tr key={item.id} className="border-b border-[#1d1826] text-[#eee8f5]">
                <td className="whitespace-nowrap px-4 py-3 font-bold">{formatDate(item.resetAt)}</td>
                <td className="px-4 py-3 font-mono text-xs text-[#b7a9c4]">{item.createdBy}</td>
                <td className="px-4 py-3 text-[#d6c9df]">{item.note || '—'}</td>
              </tr>
            ))}
          </tbody>
        </table>
        {!isLoading && (overview?.resets || []).length === 0 && !error && (
          <p className="px-4 py-10 text-center text-sm text-[#8f829c]">Sin resets: la temporada actual cuenta desde el inicio.</p>
        )}
      </div>
    </section>
  );
}
